'use client';
import React, { useState, useEffect } from 'react';
import { BookOpen, Users, Music, FileText } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useBrandLogos } from '@/hooks/useBrandLogos';
import { createClient } from '@/lib/supabase/client';

// Program dimensions: 5.75 x 8.75 inches → ratio 0.657
// At display width 192px → height = 192 / 0.657 ≈ 292px
// Header = 20% of 292 ≈ 58px | Content = 80% ≈ 234px

const PROG_W = 192;
const PROG_H = Math.round(PROG_W / (5.75 / 8.75)); // ≈ 292
const HEADER_H = Math.round(PROG_H * 0.2); // ≈ 58
const CONTENT_H = PROG_H - HEADER_H; // ≈ 234

function CoverSection({ logoWordmarkUrl, coverImageUrl }: { logoWordmarkUrl: string; coverImageUrl: string | null }) {
  return (
    <div className="bg-white flex flex-col font-sans" style={{ width: PROG_W, height: PROG_H }}>
      {/* 20% black header */}
      <div className="flex items-center justify-center gap-2 px-4 shrink-0" style={{ height: HEADER_H, backgroundColor: '#09090b' }}>
        {logoWordmarkUrl ? (
          <img
            src={logoWordmarkUrl}
            alt="Ovation"
            className="object-contain"
            style={{ height: '16.8px', filter: 'brightness(0) saturate(100%) invert(1) sepia(1) saturate(2) hue-rotate(0deg)', maxWidth: '80%' }}
          />
        ) : (
          <>
            <BookOpen size={12} className="text-amber-400" />
            <span className="text-amber-400 font-bold tracking-[0.2em] uppercase" style={{ fontSize: 9 }}>Ovation</span>
          </>
        )}
      </div>
      {/* 80% content — full-bleed archive cover image */}
      <div className="overflow-hidden bg-zinc-100 flex items-center justify-center" style={{ height: CONTENT_H }}>
        {coverImageUrl ? (
          <img
            src={coverImageUrl}
            alt="Grease musical cover"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full bg-zinc-900">
            <BookOpen size={24} className="text-amber-400 mb-2" />
            <span className="text-amber-400 font-bold tracking-widest uppercase" style={{ fontSize: 10 }}>Grease</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DemoPreview() {
  const [active, setActive] = useState('demo-cover');
  const { logoWordmarkUrl } = useBrandLogos();
  const [greaseCoverUrl, setGreaseCoverUrl] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('musicals')
      .select('cover_image_url')
      .eq('slug', 'grease')
      .single()
      .then(({ data }) => {
        if (data?.cover_image_url) {
          setGreaseCoverUrl(data.cover_image_url);
        }
      });
  }, []);

  const sections = [
    {
      id: 'demo-cover',
      label: 'Cover',
      icon: BookOpen,
      content: <CoverSection logoWordmarkUrl={logoWordmarkUrl} coverImageUrl={greaseCoverUrl} />,
    },
    {
      id: 'demo-billing',
      label: 'Billing',
      icon: FileText,
      content: (
        <div className="bg-white flex flex-col font-sans" style={{ width: PROG_W, height: PROG_H }}>
          {/* White section name header */}
          <div className="flex items-center justify-center px-4 shrink-0 border-b border-zinc-100" style={{ height: HEADER_H }}>
            <span className="text-zinc-800 font-bold tracking-[0.2em] uppercase" style={{ fontSize: 9 }}>Billing</span>
          </div>
          {/* Content */}
          <div className="flex flex-col items-center justify-center px-5" style={{ height: CONTENT_H }}>
            <div className="text-center space-y-1.5">
              <div className="tracking-widest uppercase text-zinc-500 font-sans" style={{ fontSize: 8 }}>Westside High School Theatre</div>
              <div className="text-zinc-400 font-sans" style={{ fontSize: 7 }}>Austin, Texas</div>
              <div className="tracking-widest uppercase text-zinc-600 font-sans my-2" style={{ fontSize: 8 }}>PRESENTS</div>
              <div className="font-bold text-zinc-900" style={{ fontSize: 18 }}>Grease</div>
              <div className="text-zinc-500 font-sans leading-relaxed max-w-[160px] mx-auto" style={{ fontSize: 7 }}>
                Book, Music and Lyrics by Jim Jacobs & Warren Casey
              </div>
              <div className="border-t border-zinc-200 pt-2 mt-2">
                <div className="text-zinc-500 font-sans" style={{ fontSize: 7 }}>Directed by</div>
                <div className="font-semibold text-zinc-800" style={{ fontSize: 9 }}>Ms. Rachel Torres</div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'demo-cast',
      label: 'Cast',
      icon: Users,
      content: (
        <div className="bg-white flex flex-col font-sans" style={{ width: PROG_W, height: PROG_H }}>
          {/* White section name header */}
          <div className="flex items-center justify-center px-4 shrink-0 border-b border-zinc-100" style={{ height: HEADER_H }}>
            <span className="text-zinc-800 font-bold tracking-[0.2em] uppercase" style={{ fontSize: 9 }}>Cast</span>
          </div>
          {/* Content */}
          <div className="px-4 py-3 overflow-hidden" style={{ height: CONTENT_H }}>
            <div className="text-center mb-3">
              <div className="font-bold text-zinc-900 tracking-widest uppercase" style={{ fontSize: 8 }}>Cast of Characters</div>
              <div className="w-12 h-px bg-zinc-300 mx-auto mt-1.5" />
            </div>
            <div className="space-y-1.5">
              {[
                { char: 'Sandy Olsson', actor: 'Emma Nguyen' },
                { char: 'Danny Zuko', actor: 'Marcus Johnson' },
                { char: 'Betty Rizzo', actor: 'Sofia Reyes' },
                { char: 'Kenickie', actor: 'Devon Williams' },
                { char: 'Frenchy', actor: 'Priya Nair' },
                { char: 'Teen Angel', actor: 'Tyler Brooks' },
              ]?.map((row) => (
                <div key={`cast-${row?.char}`} className="flex justify-between items-center">
                  <span className="text-zinc-700 italic" style={{ fontSize: 8 }}>{row?.char}</span>
                  <span className="text-zinc-500 font-sans" style={{ fontSize: 8 }}>{row?.actor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'demo-songs',
      label: 'Songs',
      icon: Music,
      content: (
        <div className="bg-white flex flex-col font-sans" style={{ width: PROG_W, height: PROG_H }}>
          {/* White section name header */}
          <div className="flex items-center justify-center px-4 shrink-0 border-b border-zinc-100" style={{ height: HEADER_H }}>
            <span className="text-zinc-800 font-bold tracking-[0.2em] uppercase" style={{ fontSize: 9 }}>Songs</span>
          </div>
          {/* Content */}
          <div className="px-4 py-3 overflow-hidden" style={{ height: CONTENT_H }}>
            <div className="text-center mb-3">
              <div className="font-bold text-zinc-900 tracking-widest uppercase" style={{ fontSize: 8 }}>Act One</div>
              <div className="w-12 h-px bg-zinc-300 mx-auto mt-1.5" />
            </div>
            <div className="space-y-2">
              {[
                { song: 'Summer Nights', char: 'Sandy, Danny, Company' },
                { song: "Greased Lightnin'", char: 'Danny, T-Birds' },
                { song: 'Mooning', char: 'Roger, Jan' },
                { song: "Look at Me, I'm Sandra Dee", char: 'Rizzo' },
                { song: 'We Go Together', char: 'Company' },
              ]?.map((row) => (
                <div key={`song-${row?.song}`} className="flex justify-between items-start gap-2">
                  <span className="text-zinc-700 italic flex-1" style={{ fontSize: 8 }}>{row?.song}</span>
                  <span className="text-zinc-400 font-sans text-right" style={{ fontSize: 7 }}>{row?.char}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const activeSection = sections?.find((s) => s?.id === active) || sections?.[0];
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section id="preview" className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(22,25,43,0.8) 0%, rgba(30,34,53,0.6) 100%)' }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.4), transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.3), transparent)' }} />
      </div>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div
          ref={headerRef}
          className={`text-center mb-12 ${headerVisible ? 'reveal-up delay-0' : 'reveal-hidden'}`}
        >
          <h2 className="text-4xl font-extrabold text-zinc-100 mb-3">See What You Can Build</h2>
          <p className="text-zinc-400 text-lg">Click any section to preview how it looks in your program</p>
        </div>

        <div ref={contentRef} className="flex flex-col lg:flex-row items-center gap-12 max-w-5xl mx-auto">
          {/* Section tabs */}
          <div className={`flex flex-row lg:flex-col gap-3 order-2 lg:order-1 ${contentVisible ? 'reveal-left delay-100' : 'reveal-hidden'}`}>
            {sections?.map((s) => {
              const SectionIcon = s?.icon;
              return (
                <button
                  key={s?.id}
                  onClick={() => setActive(s?.id)}
                  className={`btn-ghost flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all duration-150 ${
                    active === s?.id
                      ? 'text-zinc-950' : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                  style={
                    active === s?.id
                      ? { background: 'linear-gradient(135deg, #f59e0b, #f97316)', borderRadius: '4px' }
                      : { backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px' }
                  }
                >
                  <SectionIcon size={16} />
                  <span className="hidden sm:inline">{s?.label}</span>
                </button>
              );
            })}
          </div>

          {/* Preview */}
          <div className="order-1 lg:order-2 flex-1 flex justify-center">
            <div className="relative">
              {/* Shadow pages behind */}
              <div
                className="absolute border border-zinc-700/40 opacity-40"
                style={{ width: PROG_W, height: PROG_H, top: 12, right: -12, backgroundColor: '#252a40' }}
              />
              <div
                className="absolute border border-zinc-700/40 opacity-60"
                style={{ width: PROG_W, height: PROG_H, top: 6, right: -6, backgroundColor: '#252a40' }}
              />
              {/* Main page */}
              <div
                className="relative overflow-hidden page-turn-shadow animate-scale-in"
                style={{ width: PROG_W, height: PROG_H, border: '1px solid rgba(245,158,11,0.3)' }}
              >
                {activeSection?.content}
              </div>
              {/* Gold accent line on left edge */}
              <div className="absolute top-0 left-0 w-0.5 h-full" style={{ background: 'linear-gradient(180deg, #f59e0b, #f97316)' }} />
            </div>
          </div>

          {/* Description */}
          <div className="order-3 lg:max-w-xs">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-zinc-100">{activeSection?.label} Section</h3>
              <p className="text-zinc-400 leading-relaxed text-sm">
                Every section of your program is carefully designed and fully customizable.
                Data from our musical database auto-fills cast lists, songs, and billing credits.
              </p>
              <div className="space-y-2">
                {['Auto-filled from database', 'Fully customizable', 'Print-ready layout']?.map((feat) => (
                  <div key={`feat-${feat}`} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-gold-400" style={{ borderRadius: '1px' }} />
                    <span className="text-sm text-zinc-300">{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}